import { Controller } from "@hotwired/stimulus"
import { motionMs } from "../lib/motion"
import { subscribeToPush } from "../lib/push_subscription_service"
import { showSuccess, showWarning } from "../lib/platform_toast"
import { NativeBridge } from "../lib/native_bridge"
import { requestPermissionAndRegister } from "../lib/native_push_service"
import { afterOnboarding } from "../lib/onboarding"
import { afterConsentPrompt } from "../lib/prompt_sequence"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

/**
 * FirstVisitPromptController - First visit notification permission prompt
 *
 * Shows modal on first app entry to request push notification permission.
 * Supports both Native (FCM) and Web (Service Worker) push notifications.
 *
 * Uses NativePushService for Native apps (SSOT for API calls).
 */
export default class extends Controller {
  static targets = ["modal", "backdrop", "panel"]

  connect() {
    this.isNative = NativeBridge.isNative()
    // Never on top of /home's consent sheet: wait until it is answered
    this.cancelWait = afterOnboarding(() => {
      this.cancelConsentWait = afterConsentPrompt(() => this.checkAndShowPrompt())
    })
  }

  disconnect() {
    this.cancelWait?.()
    this.cancelConsentWait?.()
  }

  // ============================================================================
  // Prompt Display Logic
  // ============================================================================

  checkAndShowPrompt() {
    const promptStatus = localStorage.getItem("notification_prompt_shown")

    // Show if never shown or "later" expired
    if (!promptStatus) {
      this.showModal()
    } else if (promptStatus === "later") {
      const lastShown = localStorage.getItem("notification_prompt_last_shown")
      if (lastShown) {
        const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24)
        const laterDays = window.BusinessConstants?.NOTIFICATION_PROMPT_LATER_DAYS || 7
        if (daysSince > laterDays) {
          this.showModal()
        }
      }
    }
    // If 'never' or 'granted', don't show
  }

  showModal() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.backdropTarget.classList.remove("opacity-0")
      this.backdropTarget.classList.add("opacity-100")

      this.panelTarget.classList.remove("opacity-0", "scale-95")
      this.panelTarget.classList.add("opacity-100", "scale-100")
    })

    lockScroll(this, { dismiss: () => this.hideModal() })
  }

  hideModal() {
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0")

    this.panelTarget.classList.remove("opacity-100", "scale-100")
    this.panelTarget.classList.add("opacity-0", "scale-95", "pointer-events-none")

    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      unlockScroll(this)
    }, motionMs("BASE_MS"))
  }

  // ============================================================================
  // Notification Handlers
  // ============================================================================

  async enableNotifications(event) {
    event.preventDefault()

    if (this.isNative) {
      return this.enableNativeNotifications()
    }
    return this.enableWebNotifications()
  }

  /**
   * Enable notifications for Native apps (Android/iOS) via FCM
   * Uses shared NativePushService for API calls
   */
  async enableNativeNotifications() {
    try {
      const result = await requestPermissionAndRegister()

      if (result.granted) {
        localStorage.setItem("notification_prompt_shown", "granted")
        showSuccess(this.element.dataset.i18nSuccess || "推播通知已啟用！")
        this.hideModal()
      } else {
        localStorage.setItem("notification_prompt_shown", "denied")
        this.hideModal()
        if (result.error) {
          showWarning(result.error)
        } else {
          showWarning(this.element.dataset.i18nDenied || "通知權限被拒絕")
        }
      }
    } catch (error) {
      console.error("[FirstVisitPrompt] Native push error:", error)
      localStorage.setItem("notification_prompt_shown", "denied")
      this.hideModal()
    }
  }

  /**
   * Enable notifications for Web browsers via Web Push API
   */
  async enableWebNotifications() {
    if (Notification.permission === "denied") {
      localStorage.setItem("notification_prompt_shown", "denied")
      this.hideModal()
      showWarning(this.element.dataset.i18nBlocked || "通知權限已被封鎖，請在瀏覽器設定中允許。")
      return
    }

    try {
      const permission = await Notification.requestPermission()

      if (permission === "granted") {
        localStorage.setItem("notification_prompt_shown", "granted")

        const success = await subscribeToPush()
        if (success) {
          showSuccess(this.element.dataset.i18nSuccess || "推播通知已啟用！")
        }

        this.hideModal()
      } else {
        localStorage.setItem("notification_prompt_shown", "denied")
        this.hideModal()
      }
    } catch (error) {
      console.error("[FirstVisitPrompt] Web push error:", error)
      this.hideModal()
    }
  }

  // ============================================================================
  // User Actions
  // ============================================================================

  askLater(event) {
    event.preventDefault()
    localStorage.setItem("notification_prompt_shown", "later")
    localStorage.setItem("notification_prompt_last_shown", Date.now().toString())
    this.hideModal()
  }

  neverAsk(event) {
    event.preventDefault()
    localStorage.setItem("notification_prompt_shown", "never")
    this.hideModal()
  }

  backdropClick(event) {
    if (event.target === this.backdropTarget) {
      this.askLater(event)
    }
  }
}
